import React, { useMemo } from "react";
import { CheckOutlined, ClockCircleOutlined } from "@ant-design/icons";
import {
  IA_WORKFLOW_MATRIX,
  IA_WORKFLOW_ROLES,
  IA_WORKFLOW_STAGES,
} from "../../constants/internalAuditWorkflow";
import { deriveIaStageStates } from "../../utils/internalAuditView";

const STAGE_KEYS = IA_WORKFLOW_STAGES.map((s) => s.key);

function WorkflowCell({ label, state }) {
  if (!label) return <span className="text-[color:var(--color-text-muted)] font-medium">-</span>;
  if (state === "done") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-2.5 py-1.5 text-[11px] font-bold text-[color:var(--color-ok-fg)]">
        <CheckOutlined className="text-[color:var(--color-ok-fg)]" />
        <span>{label}</span>
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className="inline-flex items-center rounded-lg border-2 border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] px-2.5 py-1.5 text-[11px] font-bold text-[color:var(--color-accent)]">
        {label}
      </span>
    );
  }
  return <span className="text-[11px] font-semibold text-[color:var(--color-text-muted)]">{label}</span>;
}

/**
 * Workflow stepper + role matrix (read-only, all roles). Stage progress is derived from the stored package;
 * the Agreement stage has no record in the system so it is shown as "not tracked", never as done.
 */
export function WorkflowSubTab({ pkg }) {
  const { states, currentKey } = useMemo(() => deriveIaStageStates(pkg, STAGE_KEYS), [pkg]);
  const currentLabel = IA_WORKFLOW_STAGES.find((s) => s.key === currentKey)?.label;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm md:p-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-1 flex-wrap items-center gap-2 md:gap-0">
            {IA_WORKFLOW_STAGES.map((stage, i) => {
              const state = states[stage.key];
              const done = state === "done";
              const current = state === "current";
              return (
                <div
                  key={stage.key}
                  data-stage-state={state}
                  className="flex flex-1 min-w-[72px] flex-col items-center gap-2 px-1"
                >
                  <div className="flex w-full items-center gap-0">
                    {i > 0 ? <div className="hidden h-1 flex-1 bg-[color:var(--color-bg-alt)] md:block" /> : null}
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                        done
                          ? "border-[color:var(--color-accent-fill)] bg-[color:var(--color-accent-fill)] text-white"
                          : current
                            ? "border-[color:var(--color-accent-fill)] bg-[color:var(--color-accent-fill)] text-white"
                            : "border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] text-[color:var(--color-text-muted)]"
                      }`}
                    >
                      {done ? (
                        <CheckOutlined className="text-sm" />
                      ) : current ? (
                        <ClockCircleOutlined className="text-sm" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-[color:var(--color-bg-alt)]" />
                      )}
                    </div>
                    {i < IA_WORKFLOW_STAGES.length - 1 ? <div className="hidden h-1 flex-1 bg-[color:var(--color-bg-alt)] md:block" /> : null}
                  </div>
                  <p
                    className={`text-center text-[9px] font-black uppercase leading-tight tracking-wide ${
                      current ? "text-[color:var(--color-accent)]" : done ? "text-[color:var(--color-ok-fg)]" : "text-[color:var(--color-text-muted)]"
                    }`}
                  >
                    {stage.label}
                  </p>
                  {state === "untracked" ? (
                    <p className="text-center text-[9px] font-medium text-[color:var(--color-text-muted)]">Not tracked</p>
                  ) : null}
                </div>
              );
            })}
          </div>
          <span className="shrink-0 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            {currentLabel ? `Current: ${currentLabel}` : "All tracked stages complete"}
          </span>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-[color:var(--color-border)]">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)]">
                <th className="sticky left-0 z-10 bg-[color:var(--color-surface-sunken)] px-4 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Role
                </th>
                {IA_WORKFLOW_STAGES.map((s) => (
                  <th
                    key={s.key}
                    className="px-3 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]"
                  >
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {IA_WORKFLOW_ROLES.map((role) => (
                <tr key={role.key} className="border-b border-[color:var(--color-border)] last:border-0 hover:bg-[color:var(--color-bg-alt)]">
                  <td className="sticky left-0 z-10 bg-[color:var(--color-bg-white)] px-4 py-4 text-xs font-bold text-[color:var(--color-text-hero)]">
                    {role.label}
                  </td>
                  {IA_WORKFLOW_STAGES.map((stage) => (
                    <td key={stage.key} className="px-3 py-3 align-top">
                      <WorkflowCell label={IA_WORKFLOW_MATRIX[role.key]?.[stage.key]} state={states[stage.key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WorkflowSubTab;
